/**
 * useWSDSettings — Hook for WSD (What's Streaming Dashboard) settings management.
 *
 * Provides CRUD operations for streaming settings:
 * - Fetch current settings
 * - Update settings (Emby/Jellyfin URLs, API keys, poll interval)
 * - Test media server connections
 */
import { useCallback, useEffect, useState } from "react";

import { getCsrfToken } from "../lib/csrf";
import { toast } from "../lib/toast";
import { getServerLabel } from "../utils/serverDisplay";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type MediaServerType = "emby" | "jellyfin" | "plex";

/**
 * Per-server-type feature capability flags.
 * `true`  = feature is available; `false` = not supported on this server type.
 * Sourced from `PLEX_UNSUPPORTED` on the backend and injected into every
 * `/api/streaming/settings` response so the UI can gate buttons contract-first.
 */
export interface ServerTypeCapabilities {
  sendMessage: boolean;
  userPolicy: boolean;
  devicePolicies: boolean;
  deviceRename: boolean;
  pluginConfiguration: boolean;
  /** When `false`, terminate may 401 — surface tooltip, not a hidden button */
  terminateRequiresAdminSession: boolean;
}

export type ServerCapabilitiesMap = Record<MediaServerType, ServerTypeCapabilities>;

/** Settings DTO returned by the API (excludes API keys) */
export interface WSDSettingsDTO {
  enabled: boolean;
  pollInterval: number;
  geoIpEnabled: boolean;
  embyEnabled: boolean;
  embyUrl: string;
  embyExternalUrl: string;
  embyExternalUrlDefault: string;
  embyConfigured: boolean;
  jellyfinEnabled: boolean;
  jellyfinUrl: string;
  jellyfinExternalUrl: string;
  jellyfinExternalUrlDefault: string;
  jellyfinConfigured: boolean;
  plexEnabled: boolean;
  plexUrl: string;
  plexExternalUrl: string;
  plexConfigured: boolean;
  /** Dispatcharr base URL used to override Plex Live TV channel/program data. Empty when unset. */
  dispatcharrBaseUrl: string;
  /** `true` when both a Dispatcharr base URL and API key are stored. The API key itself is a secret and never returned. */
  dispatcharrConfigured: boolean;
  /** Per-server-type capability map. Gate UI buttons by reading this, not by hardcoding `serverType === "plex"`. */
  serverCapabilities: ServerCapabilitiesMap;
}

/** Settings update payload */
export interface WSDSettingsUpdate {
  enabled?: boolean;
  pollInterval?: number;
  geoIpEnabled?: boolean;
  embyEnabled?: boolean;
  embyUrl?: string;
  embyApiKey?: string;
  embyExternalUrl?: string;
  jellyfinEnabled?: boolean;
  jellyfinUrl?: string;
  jellyfinApiKey?: string;
  jellyfinExternalUrl?: string;
  plexEnabled?: boolean;
  plexUrl?: string;
  plexApiKey?: string;
  plexExternalUrl?: string;
  /** Dispatcharr base URL. Send a URL or `""` to clear. */
  dispatcharrBaseUrl?: string;
  /** Dispatcharr API key (secret, write-only). Send only when replacing; `""` clears the stored key. */
  dispatcharrApiKey?: string;
}

/** Connection test result */
export interface ConnectionTestResult {
  ok: boolean;
  serverName?: string;
  version?: string;
  error?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export const useWSDSettings = () => {
  const [settings, setSettings] = useState<WSDSettingsDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<MediaServerType | null>(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // Fetch Settings
  // ─────────────────────────────────────────────────────────────────────────────

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await fetch("/api/streaming/settings", {
        headers: { Accept: "application/json" },
      });

      const data = await res.json();

      if (!res.ok || !data.ok) {
        setError(data.message ?? "Failed to load streaming settings");
        return;
      }

      setSettings(data.data);
    } catch {
      setError("Failed to load streaming settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchSettings();
  }, [fetchSettings]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Update Settings
  // ─────────────────────────────────────────────────────────────────────────────

  const updateSettings = useCallback(
    async (update: WSDSettingsUpdate): Promise<boolean> => {
      try {
        setSaving(true);
        const csrfToken = getCsrfToken();

        const res = await fetch("/api/streaming/settings", {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
          },
          body: JSON.stringify(update),
        });

        const data = await res.json();

        if (!res.ok || !data.ok) {
          toast.error(data.message ?? "Failed to update settings");
          return false;
        }

        setSettings(data.data);
        toast.success("Streaming settings saved");
        return true;
      } catch {
        toast.error("Failed to update settings");
        return false;
      } finally {
        setSaving(false);
      }
    },
    []
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Test Connection
  // ─────────────────────────────────────────────────────────────────────────────

  const testConnection = useCallback(
    async (
      type: MediaServerType,
      url: string,
      apiKey: string
    ): Promise<ConnectionTestResult> => {
      try {
        setTesting(type);
        const csrfToken = getCsrfToken();

        const res = await fetch("/api/streaming/test-connection", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
          },
          body: JSON.stringify({ type, url, apiKey }),
        });

        const data = await res.json();

        // Check both the HTTP status and envelope ok flag.
        // Also check the inner data.ok in case the backend wraps the result.
        const innerResult = data.data as ConnectionTestResult | undefined;
        const succeeded = res.ok && data.ok && (innerResult?.ok !== false);

        if (!succeeded) {
          const errorMsg = data.message ?? innerResult?.error ?? "Connection test failed";
          const result: ConnectionTestResult = {
            ok: false,
            error: errorMsg,
          };
          toast.error(errorMsg);
          return result;
        }

        const result: ConnectionTestResult = {
          ok: true,
          serverName: innerResult?.serverName,
          version: innerResult?.version,
        };

        const serverLabel = getServerLabel(type);
        toast.success(`Connected to ${serverLabel}${result.serverName ? ` (${result.serverName})` : ""}`);
        return result;
      } catch {
        const result: ConnectionTestResult = {
          ok: false,
          error: "Connection test failed",
        };
        toast.error(result.error ?? "Connection test failed");
        return result;
      } finally {
        setTesting(null);
      }
    },
    []
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Return
  // ─────────────────────────────────────────────────────────────────────────────

  return {
    settings,
    loading,
    error,
    saving,
    testing,
    refresh: fetchSettings,
    updateSettings,
    testConnection,
  };
};
